import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './Button.module.css';

/**
 * **`Button`**（`docs/design-system.md` §5.1 / §5.6）。
 *
 * ## 型で守っていること
 *
 * **色・寸法の直指定 prop を持たせない**（§5.1 / §5.6）。
 * 持たせると**トークンの外の値が入る経路**ができ、
 * **4 実装に分裂して反転文字がテーマごとに割れた**という
 * 統合前の状態へ戻る。**`style` と `className` も受け取らない。**
 *
 * **`shape` を variant にしない**（§5.6）— variant は**役割の軸**であり、
 * pill は Composer 専用の**形**である。別の prop で表す。
 *
 * ## `disabled` について
 *
 * **原則使わない**（§2.7）。**使うときは理由を近くに書く**という規約なので、
 * 型からは外さない（`ButtonHTMLAttributes` 経由で渡せる）が、
 * **既定では使わない**。押せない理由が画面から読めないボタンを作らないため。
 */
export interface ButtonProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    /** **見た目を外から差し込む経路を閉じる**（§5.6: 色・寸法の直指定 prop を持たせない） */
    'className' | 'style' | 'color'
  > {
  /** **役割の軸**（形の軸ではない） */
  readonly variant?: 'primary' | 'danger' | 'subtle' | 'link';
  readonly size?: 'md' | 'sm';
  /** **Composer 専用**（§5.6。形の違いを variant にしない） */
  readonly shape?: 'default' | 'pill';
  /**
   * **「押せるが保留される」**（Composer 専用）。
   * **`disabled` の言い換えではない** — 押せるので `disabled` を立てない。
   */
  readonly pending?: boolean;
  readonly children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  shape = 'default',
  pending = false,
  children,
  type = 'button',
  ...rest
}: ButtonProps): ReactNode {
  const classes = [
    styles['button'],
    styles[variant],
    size === 'sm' ? styles['sm'] : undefined,
    shape === 'pill' ? styles['pill'] : undefined,
    pending ? styles['pending'] : undefined,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      type={type}
      className={classes}
      /**
       * **保留中であることを支援技術にも伝える。**
       * **`aria-disabled` にしない** — 押せるので嘘になる。
       */
      aria-busy={pending || undefined}
    >
      {/*
        * **Spinner の単独使用を禁じる**（§5.1）。
        * ここは **Button の `pending` に内包**した形であり、
        * **ラベルは消さない** — 無名の「処理中…」を作らないため。
        */}
      {pending ? <span className={styles['spinner']} aria-hidden="true" /> : null}
      {children}
    </button>
  );
}
